/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ModeTable from "./ModeTable";
import ModeForm from "./ModeForm";
import ModeView from "./ModeView";
import {
  useModesAdmin,
  useCreateMode,
  useUpdateMode,
  useDeleteMode,
  useToggleMode,
} from "@/hooks/api/useModes";
import type {
  Mode,
  CreateModeRequest,
  UpdateModeRequest,
} from "@/types/api.types";

export default function ModeManagement() {
  // ─── Queries & Mutations ─────────────────────────────
  const { data, isLoading, error } = useModesAdmin();
  const { mutate: createMode, isPending: isCreating } = useCreateMode();
  const { mutate: updateMode, isPending: isUpdating } = useUpdateMode();
  const { mutate: deleteMode } = useDeleteMode();
  const { mutate: toggleMode } = useToggleMode();

  // ─── State ──────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [currentMode, setCurrentMode] = useState<Partial<Mode> | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // ─── Handlers ───────────────────────────────────────

  const handleAddMode = () => {
    setIsEditing(false);
    setCurrentMode({
      name: "",
      description: "",
      logo: "",
      route: "",
      slug: "",
      isActive: true,
      order: 0,
    });
    setIsOpen(true);
  };

  const handleEditMode = (mode: Mode) => {
    setIsEditing(true);
    setCurrentMode(mode);
    setIsOpen(true);
  };

  const handleViewMode = (mode: Mode) => {
    setCurrentMode(mode);
    setViewOpen(true);
  };

  const handleDeleteMode = (id: number) => {
    if (!confirm("Are you sure you want to delete this mode?")) return;

    deleteMode(id, {
      onSuccess: () => {
        toast.success("Mode deleted successfully");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || "Failed to delete mode");
      },
    });
  };

  const handleToggleMode = (id: number) => {
    toggleMode(id, {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || "Failed to toggle mode");
      },
    });
  };

  const handleSaveMode = (formData: CreateModeRequest | UpdateModeRequest) => {
    if (isEditing && currentMode?.id) {
      // Update existing mode
      updateMode(
        { id: currentMode.id, data: formData as UpdateModeRequest },
        {
          onSuccess: () => {
            toast.success("Mode updated successfully");
            setIsOpen(false);
            setCurrentMode(null);
          },
          onError: (error: any) => {
            toast.error(
              error.response?.data?.message || "Failed to update mode",
            );
          },
        },
      );
    } else {
      // Create new mode
      createMode(formData as CreateModeRequest, {
        onSuccess: () => {
          toast.success("Mode created successfully");
          setIsOpen(false);
          setCurrentMode(null);
        },
        onError: (error: any) => {
          toast.error(error.response?.data?.message || "Failed to create mode");
        },
      });
    }
  };

  // ─── Render ─────────────────────────────────────────

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-600 mb-4">Failed to load modes</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mode Management</h1>
          <p className="text-gray-500 mt-1">Manage all your app modes</p>
        </div>
        <Button
          onClick={handleAddMode}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          disabled={isCreating}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Mode
        </Button>
      </div>

      <ModeTable
        modes={data?.modes || []}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onEdit={handleEditMode}
        onView={handleViewMode}
        onDelete={handleDeleteMode}
        onToggle={handleToggleMode}
      />

      <ModeForm
        open={isOpen}
        onOpenChange={setIsOpen}
        mode={currentMode}
        isEditing={isEditing}
        onSave={handleSaveMode}
        isSaving={isCreating || isUpdating}
      />

      <ModeView
        open={viewOpen}
        onOpenChange={setViewOpen}
        mode={currentMode as Mode}
      />
    </Layout>
  );
}
